import { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Card, Text, Stack, Box, Loader, Center, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { CohortTimeline } from '../../components/CohortTimeline';
import { coachPortalApi } from '../../services/api/coachPortal';
import type { TimelineEvent } from '../../services/api/cohortEvents';
import type {
  AvailabilityRequestWithDetails,
  InterestStatus,
  SlotAvailabilityMap,
  SlotAvailabilityStatus,
  SlotConflict,
  CoachAvailabilityResponsePayload,
} from '../../services/api/coachPortal';

import {
  PageHeader,
  CohortDetailsCard,
  InterestQuestionCard,
  DeclineConfirmationCard,
  TimeSlotAvailabilitySection,
  GroupPreferencesSection,
  SuccessConfirmation,
  PageFooter,
} from './availability';
import { getGroupCoachingDurationMinutes } from './availability/helpers';

// ============================================================================
// Helpers
// ============================================================================

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ============================================================================
// Main Component
// ============================================================================

export function CoachAvailabilityResponsePage() {
  const navigate = useNavigate();
  const { requestId: urlParam } = useParams<{ requestId: string }>();

  // Determine if the URL param is a UUID (from dashboard) or an availability token (from email link)
  const isUuid = urlParam ? UUID_REGEX.test(urlParam) : false;

  // State
  const [request, setRequest] = useState<AvailabilityRequestWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Form state
  const [interestStatus, setInterestStatus] = useState<InterestStatus | null>(null);
  const [slotAvailability, setSlotAvailability] = useState<SlotAvailabilityMap>({});
  const [maxGroups, setMaxGroups] = useState<number | null>(null);
  const [isFlexibleGroups, setIsFlexibleGroups] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [timelineTotalEvents, setTimelineTotalEvents] = useState(0);

  // Fetch request data
  const fetchRequest = useCallback(async () => {
    if (!urlParam) return;
    try {
      setIsLoading(true);

      // Use token-based endpoint for email links, UUID-based for dashboard navigation
      const data = isUuid
        ? await coachPortalApi.getAvailabilityRequestById(urlParam)
        : await coachPortalApi.getAvailabilityRequestByToken(urlParam);
      setRequest(data);

      // Fetch timeline using the resolved request ID
      const resolvedId = data.id;
      coachPortalApi
        .getAvailabilityRequestTimeline(resolvedId)
        .then(timeline => {
          setTimelineEvents(timeline.events);
          setTimelineTotalEvents(timeline.total_events);
        })
        .catch(err => {
          console.error('Failed to fetch timeline:', err);
        });

      // Pre-populate form if already responded
      if (data.interest_status) {
        setInterestStatus(data.interest_status);
      }
      if (data.slot_availability) {
        setSlotAvailability(data.slot_availability);
      }
      if (data.is_flexible_groups) {
        setIsFlexibleGroups(true);
      } else if (data.max_groups) {
        setMaxGroups(data.max_groups);
      }
      if (data.additional_notes) {
        setAdditionalNotes(data.additional_notes);
      }
    } catch (error) {
      console.error('Failed to fetch request:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load availability request',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  }, [urlParam, isUuid]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  // Handle slot availability change
  const handleSlotChange = useCallback(
    (slotId: string, status: SlotAvailabilityStatus, conflicts?: SlotConflict[]) => {
      setSlotAvailability(prev => ({
        ...prev,
        [slotId]: {
          status,
          conflicts: status === 'partial' ? conflicts || [] : undefined,
        },
      }));
    },
    [],
  );

  // Form validation
  const isValid = useMemo(() => {
    if (!interestStatus) return false;

    if (interestStatus === 'not_interested') return true;

    // If interested, need at least one available slot and either max groups or flexible
    const hasAvailableSlot = Object.values(slotAvailability).some(
      s => s.status === 'full' || s.status === 'partial',
    );
    const hasGroupPreference = isFlexibleGroups || (maxGroups !== null && maxGroups >= 1);
    return hasAvailableSlot && hasGroupPreference;
  }, [interestStatus, slotAvailability, maxGroups, isFlexibleGroups]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!urlParam || !interestStatus) return;

    try {
      setIsSubmitting(true);

      const payload: CoachAvailabilityResponsePayload = {
        interest_status: interestStatus,
      };

      if (interestStatus === 'interested') {
        payload.slot_availability = slotAvailability;
        if (isFlexibleGroups) {
          payload.is_flexible_groups = true;
        } else {
          payload.max_groups = maxGroups || undefined;
        }
        payload.additional_notes = additionalNotes || undefined;
      }

      // Use token-based submit for email links, UUID-based for dashboard navigation
      if (isUuid) {
        await coachPortalApi.submitResponse(urlParam, payload);
      } else {
        await coachPortalApi.submitResponseByToken(urlParam, payload);
      }
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit response:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to submit your response. Please try again.',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [urlParam, isUuid, interestStatus, slotAvailability, maxGroups, isFlexibleGroups, additionalNotes]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate('/coach');
  }, [navigate]);

  const handleClose = useCallback(() => {
    navigate('/coach');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // Not found state
  if (!request) {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Card withBorder padding="xl" ta="center">
          <Text fw={500} mb="md">
            Availability request not found
          </Text>
          <Button onClick={handleBack}>Return to Dashboard</Button>
        </Card>
      </Center>
    );
  }

  // MED-918 AC6: cancelled state. When the CM removes a coach from the
  // cohort, the AC4 cascade transitions any pending availability request
  // to status='cancelled'. The row stays `is_deleted = false` so this
  // page (which loads via the magic link in the original email) can
  // render a clear terminal message instead of looking like a still-open
  // request or 404'ing. Coaches should understand no further action is
  // needed on their part.
  if (request.status === 'cancelled') {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Container size="sm">
          <Card withBorder padding="xl" ta="center">
            <Stack gap="md" align="center">
              <Text fw={600} size="lg">
                This availability request has been cancelled
              </Text>
              <Text c="dimmed">
                The cohort manager for{' '}
                <Text component="span" fw={500}>
                  {request.cohort_name}
                </Text>{' '}
                cancelled this request. No action is needed on your part.
              </Text>
              <Button onClick={handleBack} mt="sm">
                Return to Dashboard
              </Button>
            </Stack>
          </Card>
        </Container>
      </Center>
    );
  }

  // MDF-233: superseded state. A coach can still hold the email for a request
  // that has since been replaced, because the cohort manager changed the ask.
  // That old link must not open the form: every manager-facing screen reads
  // only the newest request, so an answer written here would be filed against
  // a request nobody reads, and the coach would believe they were done.
  //
  // Like the cancelled state above, the row stays `is_deleted = false` so this
  // page can explain itself instead of 404'ing, and point at the live request.
  if (request.status === 'superseded') {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Container size="sm">
          <Card withBorder padding="xl" ta="center">
            <Stack gap="md" align="center">
              <Text fw={600} size="lg">
                This availability request was replaced
              </Text>
              <Text c="dimmed">
                The cohort manager for{' '}
                <Text component="span" fw={500}>
                  {request.cohort_name}
                </Text>{' '}
                changed what they are asking for, so this request is no longer
                valid. A newer request is waiting on your dashboard. Please answer
                that one instead.
              </Text>
              {/* handleBack goes to the dashboard, which is where the newer
                  request sits. The label names the real destination. */}
              <Button onClick={handleBack} mt="sm">
                Go to my dashboard
              </Button>
            </Stack>
          </Card>
        </Container>
      </Center>
    );
  }

  // Success state
  if (isSubmitted) {
    return (
      <Container size="md" py="xl">
        <SuccessConfirmation cohortName={request.cohort_name} onReturn={handleBack} />
      </Container>
    );
  }

  const showInterestedSections = interestStatus === 'interested';
  const showDeclineConfirmation = interestStatus === 'not_interested';

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        cohortName={request.cohort_name}
        onBack={handleBack}
        onClose={handleClose}
      />

      <Container size="md" py="lg" style={{ flex: 1 }}>
        <Stack gap="lg">
          <CohortDetailsCard request={request} />
          {timelineEvents.length > 0 && (
            <CohortTimeline
              events={timelineEvents}
              totalEvents={timelineTotalEvents}
              defaultExpanded={true}
            />
          )}
          <InterestQuestionCard
            value={interestStatus}
            onChange={setInterestStatus}
          />

          {showDeclineConfirmation && <DeclineConfirmationCard />}

          {showInterestedSections && (
            <>
              <TimeSlotAvailabilitySection
                timeSlots={request.time_slots}
                slotAvailability={slotAvailability}
                onChange={handleSlotChange}
                durationMinutes={getGroupCoachingDurationMinutes(request)}
              />
              <GroupPreferencesSection
                maxGroups={maxGroups}
                isFlexibleGroups={isFlexibleGroups}
                sessionCount={request.session_count}
                additionalNotes={additionalNotes}
                onMaxGroupsChange={setMaxGroups}
                onFlexibleGroupsChange={setIsFlexibleGroups}
                onNotesChange={setAdditionalNotes}
              />
            </>
          )}
        </Stack>
      </Container>

      <PageFooter
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleBack}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}

export default CoachAvailabilityResponsePage;
